
/**
 * Audio Service for decoding and playing raw PCM audio from Gemini API.
 */

// Gemini TTS typically returns 24kHz audio
const SAMPLE_RATE = 24000;

export const AudioPlayer = {
  ctx: null as AudioContext | null,

  getContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
        latencyHint: 'interactive'
      });
    }
    return this.ctx;
  },

  /**
   * Decodes a Base64 string containing raw PCM data into an AudioBuffer.
   * Uses Fetch API to offload Base64 decoding from the main thread loop.
   */
  async decodePCM(base64Data: string): Promise<AudioBuffer> {
    const ctx = this.getContext();
    
    // 1. Use fetch to convert Base64 to ArrayBuffer efficiently
    // This avoids blocking the main thread with a large atob() loop
    const res = await fetch(`data:application/octet-stream;base64,${base64Data}`);
    const arrayBuffer = await res.arrayBuffer();

    // 2. Convert to Int16 (Gemini returns 16-bit PCM)
    const dataInt16 = new Int16Array(arrayBuffer);
    
    // 3. Create AudioBuffer (Mono)
    const buffer = ctx.createBuffer(1, dataInt16.length, SAMPLE_RATE);
    const channelData = buffer.getChannelData(0);

    // 4. Normalize Int16 to Float32 (-1.0 to 1.0)
    // This loop is fast enough for typical speech segments
    for (let i = 0; i < dataInt16.length; i++) {
      channelData[i] = dataInt16[i] / 32768.0;
    }

    return buffer;
  },

  /**
   * Plays an audio buffer and returns a function to stop it.
   * Includes a smooth fade-out to prevent audio clicking on stop.
   */
  playBuffer(buffer: AudioBuffer, onEnded?: () => void): () => void {
    const ctx = this.getContext();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(1.0, ctx.currentTime);

    source.connect(gainNode);
    gainNode.connect(ctx.destination);

    source.onended = () => {
      if (onEnded) onEnded();
    };

    source.start(0);

    return () => {
      try {
        // Smooth fade out over 0.1s to prevent clicking
        gainNode.gain.cancelScheduledValues(ctx.currentTime);
        gainNode.gain.setValueAtTime(gainNode.gain.value, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
        
        source.stop(ctx.currentTime + 0.1);
        
        setTimeout(() => {
          source.disconnect();
          gainNode.disconnect();
        }, 150);
      } catch (e) {
        // Ignore errors if already stopped
      }
    };
  },

  /**
   * Resume context if suspended (browser policy)
   */
  async resume() {
    const ctx = this.getContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
  }
};
